import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import axiosInstance from '../utils/axiosInstance';

function ClearDataModal({ student, onClose, onSuccess }) {
    const [activeTab, setActiveTab] = useState('attendance'); // 'attendance' or 'submissions'
    const [attendanceLoading, setAttendanceLoading] = useState(false);
    const [submissionsLoading, setSubmissionsLoading] = useState(false);
    const [attendanceLoaded, setAttendanceLoaded] = useState(false);
    const [submissionsLoaded, setSubmissionsLoaded] = useState(false);
    const [selectedBatches, setSelectedBatches] = useState([]); // react-select multi options
    const [allAttendanceRecords, setAllAttendanceRecords] = useState([]);
    const [allSubmissionRecords, setAllSubmissionRecords] = useState([]);
    const [selectedAttendance, setSelectedAttendance] = useState([]);
    const [selectedSubmissions, setSelectedSubmissions] = useState([]);
    const [attendanceSearchDate, setAttendanceSearchDate] = useState('');
    const [attendanceSearchLecturer, setAttendanceSearchLecturer] = useState('');
    const [attendanceSearchSubject, setAttendanceSearchSubject] = useState('');

    const fetchAttendance = async () => {
        try {
            setAttendanceLoading(true);
            const response = await axiosInstance.get(`/admin/students/${student.studentId}/attendance`);
            setAllAttendanceRecords(response.data.attendance || []);
            setAttendanceLoaded(true);
        } catch (err) {
            console.error('Failed to fetch attendance:', err);
            setAllAttendanceRecords([]);
        } finally {
            setAttendanceLoading(false);
        }
    };

    const fetchSubmissions = async () => {
        try {
            setSubmissionsLoading(true);
            const response = await axiosInstance.get(`/admin/students/${student.studentId}/submissions`);
            setAllSubmissionRecords(response.data.submissions || []);
            setSubmissionsLoaded(true);
        } catch (err) {
            console.error('Failed to fetch submissions:', err);
            setAllSubmissionRecords([]);
        } finally {
            setSubmissionsLoading(false);
        }
    };

    // Lazy-load data when tab becomes active
    useEffect(() => {
        if (activeTab === 'attendance' && !attendanceLoaded && !attendanceLoading) {
            fetchAttendance();
        }
        if (activeTab === 'submissions' && !submissionsLoaded && !submissionsLoading) {
            fetchSubmissions();
        }
    }, [activeTab, attendanceLoaded, submissionsLoaded, attendanceLoading, submissionsLoading]);

    const coordinateCourseIds = React.useMemo(() => {
        const raw = student.coordinateCourseIds || [];
        if (!raw || raw.length === 0) return [];
        return raw
            .map((id) => {
                const num = Number(id);
                return Number.isFinite(num) ? num : null;
            })
            .filter((id) => id !== null);
    }, [student.coordinateCourseIds]);

    const isCourseAllowed = React.useCallback(
        (courseId) => {
            if (!coordinateCourseIds || coordinateCourseIds.length === 0) return true;
            const cid = Number(courseId);
            if (!Number.isFinite(cid)) return false;
            return coordinateCourseIds.includes(cid);
        },
        [coordinateCourseIds]
    );

    // When underlying data reloads, reset selection state
    useEffect(() => {
        setSelectedBatches([]);
        setSelectedAttendance([]);
        setSelectedSubmissions([]);
        setAttendanceSearchDate('');
        setAttendanceSearchLecturer('');
        setAttendanceSearchSubject('');
        setAllAttendanceRecords([]);
        setAllSubmissionRecords([]);
        setAttendanceLoaded(false);
        setSubmissionsLoaded(false);
    }, [student.studentId]);

    // Extract unique batches from attendance records (limited to coordinated courses when provided)
    const attendanceBatches = React.useMemo(() => {
        const batchMap = new Map();
        allAttendanceRecords.forEach(record => {
            if (!record.batchId) return;

            // If coordinator restrictions are defined, enforce them using courseId when available
            if (coordinateCourseIds && coordinateCourseIds.length > 0) {
                const rawCourseId = record.courseId || record.course_id;
                if (!rawCourseId) return;
                if (!isCourseAllowed(rawCourseId)) return;
            }

            if (!batchMap.has(record.batchId)) {
                batchMap.set(record.batchId, {
                    batchId: record.batchId,
                    batchName: record.batchName,
                    batchStatus: record.batchStatus,
                    courseShortName: record.courseShortName,
                    courseFullName: record.courseFullName
                });
            }
        });
        return Array.from(batchMap.values());
    }, [allAttendanceRecords, isCourseAllowed, coordinateCourseIds]);

    // Extract unique batches from submission records (limited to coordinated courses when provided)
    const submissionBatches = React.useMemo(() => {
        const batchMap = new Map();
        allSubmissionRecords.forEach(record => {
            if (!record.batchId) return;

            // If coordinator restrictions are defined, enforce them using courseId when available
            if (coordinateCourseIds && coordinateCourseIds.length > 0) {
                const rawCourseId = record.courseId || record.course_id;
                if (!rawCourseId) return;
                if (!isCourseAllowed(rawCourseId)) return;
            }

            if (!batchMap.has(record.batchId)) {
                batchMap.set(record.batchId, {
                    batchId: record.batchId,
                    batchName: record.batchName,
                    batchStatus: record.batchStatus,
                    courseShortName: record.courseShortName,
                    courseFullName: record.courseFullName
                });
            }
        });
        return Array.from(batchMap.values());
    }, [allSubmissionRecords, isCourseAllowed, coordinateCourseIds]);

    // Batches available for the currently active tab
    const currentTabBatches = React.useMemo(() => {
        const source = activeTab === 'attendance' ? attendanceBatches : submissionBatches;
        const batchMap = new Map();
        source.forEach((batch) => {
            if (!batch || !batch.batchId) return;
            if (!batchMap.has(batch.batchId)) {
                batchMap.set(batch.batchId, batch);
            }
        });
        return Array.from(batchMap.values());
    }, [activeTab, attendanceBatches, submissionBatches]);

    // When tab changes, clear selected batches that don't exist for that tab
    useEffect(() => {
        if (!selectedBatches || selectedBatches.length === 0) return;
        const allowedIds = new Set(currentTabBatches.map((b) => b.batchId));
        const filtered = selectedBatches.filter((opt) => allowedIds.has(opt.value));
        if (filtered.length !== selectedBatches.length) {
            setSelectedBatches(filtered);
        }
    }, [activeTab, currentTabBatches, selectedBatches]);

    // Filter records based on selected batch and search filters
    const filteredAttendanceRecords = React.useMemo(() => {
        return allAttendanceRecords.filter(record => {
            if (coordinateCourseIds && coordinateCourseIds.length > 0) {
                const rawCourseId = record.courseId || record.course_id;
                if (!rawCourseId) return false;
                if (!isCourseAllowed(rawCourseId)) return false;
            }

            // When batches are selected, include only those batches; otherwise include all batches
            if (selectedBatches && selectedBatches.length > 0) {
                const allowedIds = selectedBatches.map((b) => b.value);
                if (!allowedIds.includes(record.batchId)) return false;
            }

            // Date filter
            if (attendanceSearchDate && record.date) {
                const recordDate = new Date(record.date).toLocaleDateString('en-GB').toLowerCase();
                if (!recordDate.includes(attendanceSearchDate.toLowerCase())) return false;
            }
            
            // Lecturer filter
            if (attendanceSearchLecturer) {
                const lecturerName = (record.lecturerName || '').toLowerCase();
                if (!lecturerName.includes(attendanceSearchLecturer.toLowerCase())) return false;
            }
            
            // Subject filter
            if (attendanceSearchSubject) {
                const subjectName = (record.subjectName || '').toLowerCase();
                if (!subjectName.includes(attendanceSearchSubject.toLowerCase())) return false;
            }
            
            return true;
        });
    }, [allAttendanceRecords, selectedBatches, attendanceSearchDate, attendanceSearchLecturer, attendanceSearchSubject, isCourseAllowed, coordinateCourseIds]);

    const filteredSubmissionRecords = React.useMemo(() => {
        return allSubmissionRecords.filter(record => {
            if (coordinateCourseIds && coordinateCourseIds.length > 0) {
                const rawCourseId = record.courseId || record.course_id;
                if (!rawCourseId) return false;
                if (!isCourseAllowed(rawCourseId)) return false;
            }

            // When batches are selected, include only those batches; otherwise include all batches
            if (selectedBatches && selectedBatches.length > 0) {
                const allowedIds = selectedBatches.map((b) => b.value);
                if (!allowedIds.includes(record.batchId)) return false;
            }

            return true;
        });
    }, [allSubmissionRecords, selectedBatches, isCourseAllowed, coordinateCourseIds]);

    const handleSelectAllAttendance = (checked) => {
        if (checked) {
            setSelectedAttendance(filteredAttendanceRecords.map(a => a.attendanceId));
        } else {
            setSelectedAttendance([]);
        }
    };

    const handleSelectAttendance = (attendanceId, checked) => {
        if (checked) {
            setSelectedAttendance(prev => [...prev, attendanceId]);
        } else {
            setSelectedAttendance(prev => prev.filter(id => id !== attendanceId));
        }
    };

    const handleSelectAllSubmissions = (checked) => {
        if (checked) {
            setSelectedSubmissions(filteredSubmissionRecords.map(s => s.id));
        } else {
            setSelectedSubmissions([]);
        }
    };

    const handleSelectSubmission = (submissionId, checked) => {
        if (checked) {
            setSelectedSubmissions(prev => [...prev, submissionId]);
        } else {
            setSelectedSubmissions(prev => prev.filter(id => id !== submissionId));
        }
    };

    const handleDeleteAttendance = async () => {
        if (selectedAttendance.length === 0) {
            await Swal.fire({
                title: 'No Selection',
                text: 'Please select at least one attendance record to delete.',
                icon: 'warning',
                confirmButtonColor: '#64748b'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Delete Attendance Records?',
            text: `You are about to delete ${selectedAttendance.length} attendance record(s). This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        try {
            await axiosInstance.delete('/admin/attendance/bulk', {
                data: { attendanceIds: selectedAttendance }
            });
            await Swal.fire({
                title: 'Deleted!',
                text: `${selectedAttendance.length} attendance record(s) have been deleted.`,
                icon: 'success',
                confirmButtonColor: '#64748b'
            });
            setSelectedAttendance([]);
            fetchAttendance();
            if (typeof onSuccess === 'function') {
                onSuccess();
            }
        } catch (err) {
            await Swal.fire({
                title: 'Error',
                text: err.response?.data?.message || 'Failed to delete attendance records',
                icon: 'error',
                confirmButtonColor: '#64748b'
            });
        } finally {
        }
    };

    const handleDeleteSubmissions = async () => {
        if (selectedSubmissions.length === 0) {
            await Swal.fire({
                title: 'No Selection',
                text: 'Please select at least one submission to delete.',
                icon: 'warning',
                confirmButtonColor: '#64748b'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Delete Submissions?',
            text: `You are about to delete ${selectedSubmissions.length} submission(s). This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        try {
            await axiosInstance.delete('/admin/submissions/bulk', {
                data: { submissionIds: selectedSubmissions }
            });
            await Swal.fire({
                title: 'Deleted!',
                text: `${selectedSubmissions.length} submission(s) have been deleted.`,
                icon: 'success',
                confirmButtonColor: '#64748b'
            });
            setSelectedSubmissions([]);
            fetchSubmissions();
            if (typeof onSuccess === 'function') {
                onSuccess();
            }
        } catch (err) {
            await Swal.fire({
                title: 'Error',
                text: err.response?.data?.message || 'Failed to delete submissions',
                icon: 'error',
                confirmButtonColor: '#64748b'
            });
        } finally {
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-GB');
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return 'N/A';
        return timeStr;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
            <div className="w-full max-w-6xl bg-white rounded-lg shadow-2xl my-8">
                {/* Header */}
                <div className="border-b-2 border-slate-300 bg-slate-100 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">Clear Student Data</h2>
                            <p className="text-sm text-slate-600 mt-1">{student.fullName} (ID: {student.studentId})</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-lg p-2 text-slate-600 hover:bg-slate-200 transition-colors"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 bg-gray-50">
                    <div className="flex px-6">
                        <button
                            onClick={() => setActiveTab('attendance')}
                            className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                                activeTab === 'attendance'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                </svg>
                                Clear Attendance
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('submissions')}
                            className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                                activeTab === 'submissions'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Clear Submissions
                            </div>
                        </button>
                    </div>
                </div>

                <div className="p-6 max-h-[calc(100vh-300px)] overflow-y-auto">
                    {/* Batch Filter */}
                    <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Select Batch to View Records
                        </label>
                        <Select
                            value={selectedBatches}
                            onChange={(value) => setSelectedBatches(value || [])}
                            options={currentTabBatches.map(batch => ({
                                value: batch.batchId,
                                // Show only batchName from fetched data for the active tab
                                label: batch.batchName || `Batch ${batch.batchId}`
                            }))}
                            placeholder="-- Select a Batch --"
                            isMulti
                            isClearable
                            isSearchable
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: '0.5rem',
                                    borderColor: '#d1d5db',
                                    '&:hover': { borderColor: '#64748b' },
                                    '&:focus': { borderColor: '#64748b', boxShadow: '0 0 0 2px rgba(100, 116, 139, 0.2)' }
                                })
                            }}
                        />
                    </div>

                    {/* Attendance Tab Content */}
                    {activeTab === 'attendance' && (
                        <div>
                            {/* Search Filters (optional, shown when at least one batch is selected for more precise filtering) */}
                            {selectedBatches && selectedBatches.length > 0 && (
                                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Filter Attendance Records</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Search by Date</label>
                                            <input
                                                type="text"
                                                value={attendanceSearchDate}
                                                onChange={(e) => setAttendanceSearchDate(e.target.value)}
                                                placeholder="DD/MM/YYYY or partial..."
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Search by Lecturer</label>
                                            <input
                                                type="text"
                                                value={attendanceSearchLecturer}
                                                onChange={(e) => setAttendanceSearchLecturer(e.target.value)}
                                                placeholder="Type lecturer name..."
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Search by Subject</label>
                                            <input
                                                type="text"
                                                value={attendanceSearchSubject}
                                                onChange={(e) => setAttendanceSearchSubject(e.target.value)}
                                                placeholder="Type subject name..."
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                        </div>
                                    </div>
                                    {(attendanceSearchDate || attendanceSearchLecturer || attendanceSearchSubject) && (
                                        <button
                                            onClick={() => {
                                                setAttendanceSearchDate('');
                                                setAttendanceSearchLecturer('');
                                                setAttendanceSearchSubject('');
                                            }}
                                            className="mt-3 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                                        >
                                            Clear all filters
                                        </button>
                                    )}
                                </div>
                            )}

                            {attendanceLoading ? (
                                <div className="text-center py-12">
                                    <div className="h-12 w-12 mx-auto mb-4 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-500"></div>
                                    <p className="text-gray-600">Loading attendance records...</p>
                                </div>
                            ) : filteredAttendanceRecords.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <p className="text-lg font-medium">No attendance records found</p>
                                </div>
                            ) : (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedAttendance.length === filteredAttendanceRecords.length && filteredAttendanceRecords.length > 0}
                                                onChange={(e) => handleSelectAllAttendance(e.target.checked)}
                                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <span className="text-sm font-semibold text-gray-700">
                                                Select All ({filteredAttendanceRecords.length} records)
                                            </span>
                                        </label>
                                        <button
                                            onClick={handleDeleteAttendance}
                                            disabled={selectedAttendance.length === 0 || attendanceLoading}
                                            className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            Delete Selected ({selectedAttendance.length})
                                        </button>
                                    </div>

                                    <div className="space-y-2">
                                        {filteredAttendanceRecords.map((record) => (
                                            <div
                                                key={record.attendanceId}
                                                className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedAttendance.includes(record.attendanceId)}
                                                    onChange={(e) => handleSelectAttendance(record.attendanceId, e.target.checked)}
                                                    className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500">Date</p>
                                                        <p className="text-sm font-medium text-gray-900">{formatDate(record.date)}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500">Time</p>
                                                        <p className="text-sm text-gray-700">{formatTime(record.startTime)} - {formatTime(record.endTime)}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500">Subject</p>
                                                        <p className="text-sm text-gray-700">{record.subjectName || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500">Status</p>
                                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                            record.status === 'Present' ? 'bg-emerald-100 text-emerald-800' :
                                                            record.status === 'Absent' ? 'bg-red-100 text-red-800' :
                                                            record.status === 'Late' ? 'bg-amber-100 text-amber-800' :
                                                            record.status === 'Excused' ? 'bg-slate-100 text-slate-700' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {record.status || 'N/A'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500">Student</p>
                                                        <p className="text-sm text-gray-700">{student.fullName || 'N/A'}</p>
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <p className="text-xs font-semibold text-gray-500">Lecturer</p>
                                                        <p className="text-sm text-gray-700">{record.lecturerName || 'N/A'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Submissions Tab Content */}
                    {activeTab === 'submissions' && (
                        <div>
                            {submissionsLoading ? (
                                <div className="text-center py-12">
                                    <div className="h-12 w-12 mx-auto mb-4 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-500"></div>
                                    <p className="text-gray-600">Loading submissions...</p>
                                </div>
                            ) : filteredSubmissionRecords.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <p className="text-lg font-medium">No submissions found</p>
                                </div>
                            ) : (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedSubmissions.length === filteredSubmissionRecords.length && filteredSubmissionRecords.length > 0}
                                                onChange={(e) => handleSelectAllSubmissions(e.target.checked)}
                                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <span className="text-sm font-semibold text-gray-700">
                                                Select All ({filteredSubmissionRecords.length} submissions)
                                            </span>
                                        </label>
                                        <button
                                            onClick={handleDeleteSubmissions}
                                            disabled={selectedSubmissions.length === 0 || submissionsLoading}
                                            className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            Delete Selected ({selectedSubmissions.length})
                                        </button>
                                    </div>

                                    <div className="space-y-2">
                                        {filteredSubmissionRecords.map((record) => (
                                            <div
                                                key={record.id}
                                                className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedSubmissions.includes(record.id)}
                                                    onChange={(e) => handleSelectSubmission(record.id, e.target.checked)}
                                                    className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500">Subject</p>
                                                        <p className="text-sm font-medium text-gray-900">{record.subjectName || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500">Semester</p>
                                                        <p className="text-sm text-gray-700">Semester {record.semester || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500">Submission Date</p>
                                                        <p className="text-sm text-gray-700">{formatDate(record.submissionDate)}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500">Status</p>
                                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                            record.submission_status === 'on-time submission' ? 'bg-green-100 text-green-800' :
                                                            record.submission_status === 'late submission' ? 'bg-yellow-100 text-yellow-800' :
                                                            record.submission_status === 're-submission' ? 'bg-blue-100 text-blue-800' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {record.submission_status || 'not submitted'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500">Marks</p>
                                                        <p className="text-sm font-medium text-gray-900">{record.marks || 'Not graded'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                    <div className="flex justify-end">
                        <button
                            onClick={onClose}
                            className="rounded-lg border border-gray-300 bg-white px-6 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ClearDataModal;
